import React, {
  memo,
  useRef,
  useImperativeHandle,
  forwardRef,
  useEffect,
  useMemo,
} from "react";
import { Sprite } from "@pixi/react";
import * as PIXI from "pixi.js";
import { Settings } from "../../../settings";
import { OVERLAY_BACKGROUND_COLOR } from "../../Shared/Overlay/OverlayController";

// The per-reel TENSION dim, and 0.6 matches Games/Main/DimReelOverlay exactly —
// that is this file's counterpart, not DimReelSet. Do not "align" it with
// DimReelSet's number: that is the whole-board dim behind a win presentation,
// a different effect that both rooms already share from one file.
// const DIMMED_ALPHA = 0.6;
const DIMMED_ALPHA = 0.75; // matches DimReelSet (2026-10-01)

// Per-reel dimming overlay for tension mode and other effects
const FreeSpinBasicDimReelOverlay = memo(
  forwardRef(({ symbolWidth, symbolHeight }, ref) => {
    const dimSpriteRef = useRef(null);
    const currentAlphaRef = useRef(0); // Track current alpha to persist across re-renders

    // Memoize sprite dimensions and position
    const spriteDimensions = useMemo(
      () => ({
        width: symbolWidth,
        height: symbolHeight * Settings.SYMBOLS_VISIBLE,
        y: symbolHeight * Settings.SYMBOLS_VISIBLE * 1.5 + symbolHeight * 0.5,
      }),
      [symbolWidth, symbolHeight]
    );

    // Expose dimming control methods through ref
    useImperativeHandle(
      ref,
      () => ({
        // Dim the reel (show overlay)
        dimReel: () => {
          if (!dimSpriteRef.current) return;
          currentAlphaRef.current = DIMMED_ALPHA;
          dimSpriteRef.current.alpha = DIMMED_ALPHA;
        },

        // Remove dimming (clear overlay)
        undimReel: () => {
          if (!dimSpriteRef.current) return;
          if (currentAlphaRef.current === 0) return;
          currentAlphaRef.current = 0;
          dimSpriteRef.current.alpha = 0;
        },
      }),
      []
    );

    // Preserve alpha state across re-renders (especially during window resize)
    useEffect(() => {
      if (dimSpriteRef.current && currentAlphaRef.current !== 0) {
        dimSpriteRef.current.alpha = currentAlphaRef.current;
      }
    }, [symbolWidth, symbolHeight]);

    return (
      <Sprite
        ref={dimSpriteRef}
        texture={PIXI.Texture.WHITE}
        width={spriteDimensions.width}
        height={spriteDimensions.height}
        anchor={0.5}
        tint={OVERLAY_BACKGROUND_COLOR}
        alpha={0}
        y={spriteDimensions.y}
        name="fs_dim_reel_sprite"
      />
    );
  })
);

FreeSpinBasicDimReelOverlay.displayName = "FreeSpinBasicDimReelOverlay";

export default FreeSpinBasicDimReelOverlay;
